import { nextPage } from "@/api/paging";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import {
  ApiError,
  deleteRole,
  getRole,
  listPermissions,
  listUsers,
  setRolePermissions,
  updateRole,
  type Role,
  type RoleUpdate,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { useConfirmFocus } from "@/components/confirmFocus";
import { Field, inputClass, serverProblems, submitClass } from "@/components/form";
import { DetailField, EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { groupPermissions, holderCount } from "@/features/roles/permissions";
import { SystemBadge } from "@/features/roles/RoleMatrix";
import { ConflictBanner } from "@/features/users/conflicts";

const detailApi = getRouteApi("/staff/roles/$roleId");

export function RoleDetailPage() {
  const { roleId } = detailApi.useParams();
  const role = useQuery({ queryKey: ["role", roleId], queryFn: () => getRole(roleId) });

  // 404 is an unknown id, 422 a malformed one; both mean the link points at nothing.
  if (role.error instanceof ApiError && [404, 422].includes(role.error.status)) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Role not found" detail="No role matches this link." />
        <Link to="/roles" className="text-sm font-medium text-zinc-950 underline">
          Back to roles
        </Link>
      </section>
    );
  }
  if (!role.data) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Role" detail={roleId} />
        <ErrorBanner error={role.error} />
        {role.isLoading ? <EmptyState title="Loading role" detail="Fetching the role." /> : null}
      </section>
    );
  }
  return <RoleView role={role.data} />;
}

const HOLDERS_LIMIT = 50;

function RoleView({ role }: { role: Role }) {
  const { me, can } = useSession();
  const queryClient = useQueryClient();
  const manage = can("role.manage");
  const holders = useQuery({
    queryKey: ["users", { role: role.id, holders: true }],
    queryFn: () => listUsers({ roleId: role.id, limit: HOLDERS_LIMIT }),
    enabled: can("user.read"),
  });
  // Unknown unless every holder is listed; then we know whether the signed-in user is one.
  const holdsRole =
    holders.data && nextPage(holders.data) === undefined ? (holders.data.items ?? []).some((user) => user.id === me?.user.id) : null;

  const store = async (next: Role) => {
    // A refetch begun before the save would otherwise land after it with the old values.
    await queryClient.cancelQueries({ queryKey: ["role", role.id] });
    queryClient.setQueryData(["role", role.id], next);
    await queryClient.invalidateQueries({ queryKey: ["roles"] });
    // User pages show role names.
    await queryClient.invalidateQueries({ queryKey: ["users"] });
    await queryClient.invalidateQueries({ queryKey: ["user"] });
  };

  return (
    <section className="space-y-5">
      <WorkspaceHeader title={role.name} detail={role.description || "No description"} />
      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <dl className="grid gap-3 text-sm md:grid-cols-3">
          <DetailField label="Kind" value={role.is_system ? <SystemBadge /> : "Custom"} />
          <DetailField label="Held by" value={holderCount(role.user_count)} />
        </dl>
        {manage ? null : (
          <p className="mt-3 text-sm text-zinc-700">Requires an email sign-in code: {role.requires_two_factor ? "Yes" : "No"}</p>
        )}
      </div>
      {manage ? <EditRole role={role} onSaved={store} /> : null}
      <RolePermissions role={role} editable={manage} holdsRole={holdsRole} onSaved={store} />
      {holders.isFetched || holders.isError ? (
        <section aria-labelledby="role-holders" className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
          <h2 id="role-holders" className="text-sm font-semibold">
            Holders
          </h2>
          <ErrorBanner error={holders.error} />
          {holders.isError ? <p className="text-sm text-zinc-500">The holders couldn't be loaded.</p> : null}
          {holders.data && (holders.data.items ?? []).length === 0 ? (
            <p className="text-sm text-zinc-500">Nobody holds this role.</p>
          ) : (
            <ul className="divide-y divide-zinc-100 text-sm">
              {(holders.data?.items ?? []).map((user) => (
                <li key={user.id} className="flex justify-between gap-3 py-2">
                  <Link to="/users/$userId" params={{ userId: user.id }} className="font-medium text-zinc-950 underline-offset-2 hover:underline">
                    {user.full_name}
                  </Link>
                  <span className="text-zinc-500">{user.is_active ? user.email : `${user.email} (inactive)`}</span>
                </li>
              ))}
            </ul>
          )}
          {holders.data && nextPage(holders.data) !== undefined ? (
            <Link to="/users" search={{ role: role.id }} className="text-sm font-medium text-zinc-950 underline">
              See every holder
            </Link>
          ) : null}
        </section>
      ) : null}
      {manage ? <DeleteRole role={role} /> : null}
    </section>
  );
}

function EditRole({ role, onSaved }: { role: Role; onSaved: (next: Role) => Promise<void> }) {
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? "");
  const [requires, setRequires] = useState(role.requires_two_factor);
  const [saved, setSaved] = useState(false);
  // A refetch with another admin's change replaces the form; our own save lands on the same values.
  const signature = JSON.stringify([role.name, role.description ?? "", role.requires_two_factor]);
  const [seen, setSeen] = useState(signature);
  if (signature !== seen) {
    setSeen(signature);
    setName(role.name);
    setDescription(role.description ?? "");
    setRequires(role.requires_two_factor);
  }
  const body: RoleUpdate = {};
  if (name.trim() !== role.name) body.name = name.trim();
  if (description.trim() !== (role.description ?? "")) body.description = description.trim();
  if (requires !== role.requires_two_factor) body.requires_two_factor = requires;

  const save = useMutation({
    mutationFn: (update: RoleUpdate) => updateRole(role.id, update),
    onSuccess: async (next) => {
      setSaved(true);
      setName(next.name);
      setDescription(next.description ?? "");
      setRequires(next.requires_two_factor);
      await onSaved(next);
    },
  });
  const edited = () => {
    setSaved(false);
    if (save.error) save.reset();
  };
  const server = serverProblems(save.error, ["name", "description"]);
  const nameProblem = name.trim() === "" ? "is required" : server.byField.name;
  const blocked = Boolean(nameProblem) || Object.keys(body).length === 0;

  return (
    <form
      noValidate
      aria-label="Edit role"
      className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!blocked && !save.isPending) save.mutate(body);
      }}
    >
      <h2 className="text-sm font-semibold">Role details</h2>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Name" problem={nameProblem}>
          {(props) => (
            <input
              {...props}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                edited();
              }}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="Description" problem={server.byField.description}>
          {(props) => (
            <input
              {...props}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                edited();
              }}
              className={inputClass}
            />
          )}
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={requires}
          onChange={(e) => {
            setRequires(e.target.checked);
            edited();
          }}
        />
        Require email sign-in code
      </label>
      <ErrorBanner error={save.error} />
      <button type="submit" disabled={blocked || save.isPending} className={submitClass}>
        {save.isPending ? "Saving..." : "Save changes"}
      </button>
      {saved ? (
        <p role="status" className="text-sm font-medium text-emerald-700">
          Saved.
        </p>
      ) : null}
    </form>
  );
}

function RolePermissions({
  role,
  editable,
  holdsRole,
  onSaved,
}: {
  role: Role;
  editable: boolean;
  /** Null when we can't tell, which counts as holding it. */
  holdsRole: boolean | null;
  onSaved: (next: Role) => Promise<void>;
}) {
  const { refresh } = useSession();
  const catalog = useQuery({ queryKey: ["permissions"], queryFn: listPermissions });
  const groups = groupPermissions(catalog.data ?? []);
  const [selected, setSelected] = useState<string[]>(role.permissions);
  const [confirming, setConfirming] = useState(false);
  const [saved, setSaved] = useState(false);
  const { trigger, confirm } = useConfirmFocus(confirming);

  // The save is a full replace, so a refetch with another admin's change must replace the checklist.
  const signature = JSON.stringify([...role.permissions].sort());
  const [seen, setSeen] = useState(signature);
  if (signature !== seen) {
    setSeen(signature);
    setSelected(role.permissions);
    setConfirming(false);
  }

  const added = selected.filter((key) => !role.permissions.includes(key)).sort((a, b) => a.localeCompare(b));
  const removed = role.permissions.filter((key) => !selected.includes(key)).sort((a, b) => a.localeCompare(b));
  const changed = added.length > 0 || removed.length > 0;

  const save = useMutation({
    mutationFn: (keys: string[]) => setRolePermissions(role.id, keys),
    onSuccess: async (next) => {
      setConfirming(false);
      setSaved(true);
      setSelected(next.permissions);
      await onSaved(next);
      // The signed-in user's permissions may have changed with the role's.
      if (holdsRole !== false) await refresh();
    },
    // The checklist keeps the unsaved edits so they can be adjusted and retried.
    onError: () => setConfirming(false),
  });
  const edited = (next: string[]) => {
    setSelected(next);
    setSaved(false);
    setConfirming(false);
    if (save.error) save.reset();
  };
  const toggle = (key: string, on: boolean) => edited(on ? [...selected, key] : selected.filter((k) => k !== key));

  return (
    <form
      aria-label="Permissions"
      className="space-y-4 rounded-lg border border-zinc-200 bg-white p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (changed) setConfirming(true);
      }}
    >
      <h2 className="text-sm font-semibold">Permissions</h2>
      <ErrorBanner error={catalog.error} />
      <div className="grid gap-4 md:grid-cols-2">
        {groups.map(({ group, permissions }) => {
          const keys = permissions.map((p) => p.key);
          const count = keys.filter((key) => selected.includes(key)).length;
          return (
            <fieldset key={group} className="space-y-2 rounded-md border border-zinc-200 p-3 text-sm">
              <legend className="px-1 text-xs font-semibold uppercase text-zinc-600">{group}</legend>
              {editable ? (
                <label className="flex items-center gap-2 text-xs font-medium text-zinc-700">
                  <input
                    type="checkbox"
                    checked={count === keys.length}
                    ref={(el) => {
                      if (el) el.indeterminate = count > 0 && count < keys.length;
                    }}
                    onChange={(e) =>
                      edited(e.target.checked ? [...new Set([...selected, ...keys])] : selected.filter((key) => !keys.includes(key)))
                    }
                  />
                  All {group} permissions
                </label>
              ) : null}
              {permissions.map((permission) => (
                <label key={permission.key} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    disabled={!editable}
                    checked={selected.includes(permission.key)}
                    onChange={(e) => toggle(permission.key, e.target.checked)}
                  />
                  <span>
                    <span className="font-mono text-xs text-zinc-900">{permission.key}</span>{" "}
                    <span className="text-xs text-zinc-500">{permission.description}</span>
                  </span>
                </label>
              ))}
            </fieldset>
          );
        })}
      </div>
      {editable ? (
        <>
          <button ref={trigger} type="submit" disabled={!changed || confirming || save.isPending} className={submitClass}>
            Save permissions
          </button>
          {confirming ? (
            <div role="group" aria-label="Confirm permission change" className="space-y-2 rounded-md bg-zinc-50 p-3 text-sm text-zinc-700">
              {added.length > 0 ? <p>Adds: {added.join(", ")}</p> : null}
              {removed.length > 0 ? <p>Removes: {removed.join(", ")}</p> : null}
              <p>Affects {holderCount(role.user_count)}.</p>
              <div className="flex flex-wrap gap-2">
                <ToolbarButton tone="primary" ref={confirm} disabled={save.isPending} onClick={() => save.mutate([...selected].sort((a, b) => a.localeCompare(b)))}>
                  {save.isPending ? "Saving..." : `Save permissions for ${role.name}`}
                </ToolbarButton>
                <ToolbarButton disabled={save.isPending} onClick={() => setConfirming(false)}>
                  Cancel
                </ToolbarButton>
              </div>
            </div>
          ) : null}
          <ConflictBanner error={save.error} />
          {saved ? (
            <p role="status" className="text-sm font-medium text-emerald-700">
              Permissions saved.
            </p>
          ) : null}
        </>
      ) : null}
    </form>
  );
}

function DeleteRole({ role }: { role: Role }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const { trigger, confirm } = useConfirmFocus(confirming);
  const remove = useMutation({
    mutationFn: () => deleteRole(role.id),
    onSuccess: async () => {
      // Leave first: removing the role's query while mounted would flash "Role not found".
      await navigate({ to: "/roles" });
      queryClient.removeQueries({ queryKey: ["role", role.id] });
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
    onError: () => setConfirming(false),
  });

  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Delete</h2>
          <p className="mt-1 text-xs text-zinc-500">
            {role.is_system ? "System roles can't be deleted." : "Only a role nobody holds can be deleted."}
          </p>
        </div>
        {role.is_system ? null : (
          <ToolbarButton ref={trigger} tone="danger" disabled={confirming || remove.isPending} onClick={() => setConfirming(true)}>
            Delete role
          </ToolbarButton>
        )}
      </div>
      {confirming ? (
        <div role="group" aria-label="Confirm delete" className="flex flex-wrap items-center gap-3 rounded-md bg-zinc-50 p-3 text-sm">
          <p className="min-w-72 flex-1 text-zinc-700">Delete {role.name}? This can't be undone.</p>
          <ToolbarButton ref={confirm} tone="danger" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? "Deleting..." : `Delete ${role.name}`}
          </ToolbarButton>
          <ToolbarButton disabled={remove.isPending} onClick={() => setConfirming(false)}>
            Cancel
          </ToolbarButton>
        </div>
      ) : null}
      <ConflictBanner error={remove.error} />
    </div>
  );
}
